import { Page } from '@app/components/page/Page';
import { Unpacked } from '@mi-front/core';
import { Box } from '@mui/material';
import { MRT_TableOptions } from 'material-react-table';
import { ComponentType } from 'react';
import { ReportCtxProvider } from '../report-ctx';
import { ListingType } from '../types';
import { ListingViewResult } from './ListingViewResult';
import { FormLibrary, ReportForm } from './ReportForm';
import { ReportQueryParamsTransform, ReportViewProps } from './ReportView';
import { useReport } from './useReport';

export type ListingViewProps<R extends ListingType> = Omit<
  ReportViewProps<R>,
  'Form' | 'Result'
> & {
  Form?: ComponentType;
  Result: ComponentType<ListingResultProps<R>>;
  showEmptyResult?: boolean;
  horizontal?: boolean;
  formLibrary?: FormLibrary;
};

export type ListingResultProps<R extends ListingType> = {
  mrtProps: Omit<MRT_TableOptions<Unpacked<R['Result']['results']>>, 'columns'>;
};

/**
 * Listing view is a paginated listing fetched from the API.
 * Contrary to a report, the pagination should be done server side
 * instead of client side.
 */
export function ListingView<R extends ListingType>(p: ListingViewProps<R>) {
  const {
    Form,
    Result,
    showEmptyResult,
    horizontal,
    formLibrary,
    ResultWrapper = Page.Content,
    ...rest
  } = p;

  const ctx = useReport<{
    Values: R['Values'];
    Result: R['Result'];
  }>({
    download: false,
    ...rest,
    defaultValues: {
      ...rest.defaultValues,
      pageSize: 50,
      pageIndex: 0,
    },
    queryParamsTransform: {
      ...rest.queryParamsTransform,
      pageIndex: {
        parse: Number,
      },
      pageSize: {
        parse: Number,
      },
    } as ReportQueryParamsTransform<R>,
    // do not sync page to URL
    skipQuerySyncParams: ['pageIndex', 'pageSize'],
    runOnLoad: true,
  });

  return (
    <ReportCtxProvider value={ctx}>
      <Box mb={6}>
        {Form && (
          <ReportForm horizontal={horizontal} formLibrary={formLibrary}>
            <Form />
          </ReportForm>
        )}
        <ResultWrapper>
          <ListingViewResult Result={Result} showEmpty={showEmptyResult} />
        </ResultWrapper>
      </Box>
    </ReportCtxProvider>
  );
}
